import IReviewFormProps from './IReviewFormProps';
import React, { useRef } from 'react';
import { Alert, Col, Form, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import FormStepper from '../../Form/FormStepper/FormStepper';
import { LoadingSpinner } from '../../LoadingSpinner';
import { useSupervisorReviewFormComponent } from './hooks/useSupervisorReviewFormComponent';
import IEvaluationFormStep from 'interfaces/IEvaluationFormStep';
import { FeedbackLevel } from '../../../interfaces/FeedbackLevel';
import { useNotificationContext } from '../../../contexts/NotificationContext';
import ApplicationReviewFormatSelect from './ApplicationReviewFormatSelect';
import { ReviewFormat, ReviewStatus } from '../../../graphql/__generated__/graphql';

const PrimarySupervisorReviewForm: React.FC<IReviewFormProps> = ({
    applicationReview,
    readOnly,
    handleUpdate,
    loading,
    updating,
}) => {
    const formRef = useRef(null);
    const navigate = useNavigate();
    const notificationContext = useNotificationContext();
    const { models, operations } = useSupervisorReviewFormComponent(
        applicationReview,
        loading,
        readOnly,
        handleUpdate
    );

    const { formData, currentStep, requirements } = models;
    const {
        handleNextStep,
        handleInputChangeEvent,
        handleInputChange,
        handleSaveProgress,
        handleSetStep,
        handleSubmit,
        handleSetReviewFormat,
    } = operations;

    const handleClickSaveProgress = async () => {
        const result = await handleSaveProgress();
        handleFormFeedback(result.success ? 'info' : 'danger', result.message);
    };

    const handleFormFeedback = (level: FeedbackLevel, message: string[] | string) =>
        notificationContext.notify(level, message);

    if (!requirements || !requirements?.evaluation?.length || loading) return <LoadingSpinner />;

    if (
        !readOnly &&
        !applicationReview.accreditationApplication.isRenewal &&
        !applicationReview.reviewFormat
    ) {
        if (requirements.config?.algorithmEnabled && requirements.config?.minScore !== undefined) {
            return <ApplicationReviewFormatSelect onSelect={handleSetReviewFormat} />;
        }

        handleSetReviewFormat(ReviewFormat.Descriptive);
        return <LoadingSpinner />;
    }

    const renderStep = (Step: React.FC<IEvaluationFormStep>, stepNumber: number) => (
        <Step
            formData={formData}
            formRef={formRef}
            models={{
                applicationReview,
                loading,
                updating,
                ...models,
            }}
            onFeedback={handleFormFeedback}
            handleInputChangeEvent={handleInputChangeEvent}
            handleNext={handleNextStep}
            handleSubmit={handleSubmit}
            onInputChange={handleInputChange}
            requirementsGroup={requirements.evaluation[stepNumber - 1]}
            requirements={requirements}
            readOnly={readOnly}
            reviewFormat={applicationReview.reviewFormat || ReviewFormat.Descriptive} // default to descriptive if not set
        />
    );

    if (!models.stepsConfig.length) {
        return <LoadingSpinner />;
    }

    const stepsConfig = models.stepsConfig.slice(
        0,
        readOnly ? models.stepsConfig.length - 1 : models.stepsConfig.length
    );

    const applicantName =
        applicationReview.accreditationApplication?.member?.firstname +
        ' ' +
        applicationReview.accreditationApplication?.member?.lastname;

    return (
        <>
            <h2 className="fs-1 fw-bold mb-5">Supervisor evaluation for {applicantName}</h2>
            {!readOnly && (
                <h4>
                    View {applicantName}'s application &nbsp;
                    <a
                        href={`/accreditation/view/${applicationReview.accreditationApplication?.accreditationApplicationId}`}
                        target={'_blank'}
                        rel="noopener noreferrer"
                    >
                        here
                    </a>
                    &nbsp;(opens in a new tab)
                </h4>
            )}
            {formData.comments &&
                readOnly &&
                applicationReview.reviewStatus !== ReviewStatus.Complete && (
                    <Row>
                        <Col xs={6}>
                            <Alert variant={'warning'} className={'w-100'}>
                                <Alert.Heading>Supervisor comments</Alert.Heading>
                                {formData.comments}
                            </Alert>
                        </Col>
                    </Row>
                )}
            <Row>
                <Form
                    noValidate
                    ref={formRef}
                    onSubmit={() => {}} // does nothing
                    action={'/accreditation'}
                >
                    <FormStepper
                        stepNumber={currentStep}
                        renderStep={renderStep}
                        steps={stepsConfig}
                        formRef={formRef}
                        allowSkipStep={true}
                        handleCanSkipToStep={() => true}
                        handleFeedback={handleFormFeedback}
                        handleChangeStep={handleSetStep}
                        handleCancel={() => {
                            navigate('/accreditation');
                        }}
                        handleSubmit={(e) => {
                            // The review form doesn't use a typical form submit
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                        handleSaveProgress={handleClickSaveProgress}
                        submitted={false}
                        submitting={false}
                        allowSubmit={false}
                        readOnly={readOnly}
                        submitText={'Submit and pay'}
                        nextButtonText={readOnly ? 'Next' : 'Save and continue'}
                        className={'overflow-x-auto'}
                    ></FormStepper>
                    <Form.Control name={'formSubmitType'} value="evaluation" type={'hidden'} />
                    <Form.Control name={'formSubmitResult'} value={'complete'} type={'hidden'} />
                </Form>
            </Row>
        </>
    );
};

export default PrimarySupervisorReviewForm;
